import * as React from 'react';
import { find, map } from 'lodash';

import { Button, Typography, message } from '@revfluence/fresh';

import { logger } from '@common';
import { useRevokeResourceAccessMutation } from '@frontend/app/hooks';

const { Text } = Typography;

export interface IActiveGrant {
  id: number;
  grantedUserId: string;
  reason: string | null;
  createdDate: string;
}

interface IActiveGrantsUser {
  id: string;
  name: string;
  email: string;
}

interface IActiveGrantsCellProps {
  grants: IActiveGrant[];
  clientId: string;
  users?: IActiveGrantsUser[];
  onRevoked: () => void;
}

// The revoke/list half of EPD-10466's grant mechanism: shows who currently has temporary
// access to this mailbox and lets it be revoked — a plain delete of just that one Resource
// row, never the mailbox owner's own connection.
const { useState, useCallback } = React;

const ActiveGrantsCell: React.FC<React.PropsWithChildren<IActiveGrantsCellProps>> = React.memo(
  ({ grants, clientId, users, onRevoked }) => {
    const [pendingGrantId, setPendingGrantId] = useState<number>(undefined);
    const [revokeResourceAccess, { loading }] = useRevokeResourceAccessMutation({
      onCompleted() {
        message.success('Access revoked.');
        setPendingGrantId(undefined);
        onRevoked();
      },
      onError(error) {
        logger.error(`Failed to revoke resource access: ${error}`, error);
        message.error('Failed to revoke access. Please try refreshing.');
        setPendingGrantId(undefined);
      },
      context: {
        headers: {
          'requested-client-id': clientId,
        },
      },
    });

    const onRevoke = useCallback(
      (grantId: number) => {
        setPendingGrantId(grantId);
        revokeResourceAccess({ variables: { grantId } });
      },
      [revokeResourceAccess],
    );

    if (!grants?.length) {
      return <Text type="secondary">None</Text>;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {map(grants, (grant) => {
          const user = find(users, { id: grant.grantedUserId });
          return (
            <div key={grant.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text>{user ? `${user.name} <${user.email}>` : grant.grantedUserId}</Text>
              <Button
                size="small"
                danger
                loading={loading && pendingGrantId === grant.id}
                onClick={() => onRevoke(grant.id)}
              >
                Revoke
              </Button>
            </div>
          );
        })}
      </div>
    );
  },
);
ActiveGrantsCell.displayName = 'ActiveGrantsCell';

export default ActiveGrantsCell;
